import { useState } from 'react';
import apiService from '../services/api-service';
import { validateEmail } from '../validators';
import { AUTH_MESSAGES, CONSOLE_MESSAGES } from '../constants';
import { MODAL_CLASSES, ICON_CLASSES, ALERT_CLASSES, cn } from '../styles';

function ForgotPasswordModal({ isOpen, onClose }) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState(''); // 'success' or 'error'

  // Handle modal close
  const handleClose = () => {
    setEmail('');
    setMessage('');
    setStatus('');
    onClose();
  };

  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateEmail(email.trim());
    if (!validation.isValid) {
      setStatus('error');
      setMessage(validation.error);
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    try {
      const data = await apiService.forgotPassword(email.trim());
      setStatus('success');
      setMessage(data.message || AUTH_MESSAGES.PASSWORD_RESET_SENT);
    } catch (error) {
      console.error(CONSOLE_MESSAGES.FORGOT_PASSWORD_ERROR, error);
      setStatus('error');
      setMessage(error.message || AUTH_MESSAGES.PASSWORD_RESET_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className={MODAL_CLASSES.OVERLAY}
      onClick={handleOverlayClick}
    >
      <div className="relative w-full max-w-md bg-dark-800 rounded-xl sm:rounded-2xl shadow-2xl border border-dark-700 overflow-hidden max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={handleClose}
          disabled={isSubmitting}
          className="absolute top-3 right-3 z-10 p-1.5 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
          aria-label="Uždaryti"
        >
          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Scrollable content */}
        <div className="max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="p-4 sm:p-5 pb-2 sm:pb-3">
            <div className="text-center">
              <div className={cn(ICON_CLASSES.CONTAINER_MD, ICON_CLASSES.ORANGE_GRADIENT, 'mx-auto mb-3')}>
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-white mb-1">
                Pamiršote slaptažodį?
              </h2>
              <p className="text-gray-400 text-xs sm:text-sm">
                Įveskite el. pašto adresą slaptažodžio atkūrimui
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="px-4 sm:px-5 pb-4 sm:pb-5">
          {status === 'success' ? (
            // Success state
            <div className="space-y-3 sm:space-y-4">
              <div className={cn(ALERT_CLASSES.SUCCESS_LARGE, 'text-center')}>
                <div className="flex items-center justify-center mb-2">
                  <svg className="w-5 h-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-green-400 font-medium text-xs sm:text-sm">
                    El. laiškas išsiųstas!
                  </span>
                </div>
                <p className="text-green-300 text-xs sm:text-sm">
                  {message}
                </p>
              </div>

              <div className="bg-dark-700/30 rounded-lg p-3 sm:p-4 space-y-2 sm:space-y-3">
                <h3 className="text-white font-medium text-xs sm:text-sm flex items-center">
                  <svg className="w-4 h-4 text-primary-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Kitas žingsnis:
                </h3>
                <ul className="text-gray-400 text-xs sm:text-sm space-y-1 pl-6">
                  <li>1. Patikrinkite savo el. pašto dėžutę</li>
                  <li>2. Atidarykite el. laišką iš FilmųDB</li>
                  <li>3. Spauskite slaptažodžio atkūrimo nuorodą</li>
                  <li>4. Nustatykite naują slaptažodį</li>
                </ul>
              </div>

              <div className={ALERT_CLASSES.WARNING_LARGE}>
                <div className="flex items-start space-x-2">
                  <svg className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div>
                    <p className="text-yellow-300 text-xs sm:text-sm font-medium">
                      Svarbu!
                    </p>
                    <p className="text-yellow-200 text-[11px] sm:text-xs">
                      Nuoroda galioja tik 1 valandą. Jei nematote el. laiško, patikrinkite šlamšto aplanką.
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleClose}
                className="btn-primary w-full justify-center h-10 sm:h-11 text-sm sm:text-base"
              >
                Supratau
              </button>
            </div>
          ) : (
            // Form state
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              {/* Error message */}
              {status === 'error' && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs sm:text-sm">
                  {message}
                </div>
              )}

              {/* Email field */}
              <div className="space-y-1.5 sm:space-y-2">
                <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-300">
                  El. pašto adresas
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  className="input-primary w-full text-sm sm:text-base"
                  placeholder="Įveskite savo el. pašto adresą"
                  required
                />
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full justify-center relative disabled:opacity-50 disabled:cursor-not-allowed h-10 sm:h-11 text-sm sm:text-base"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Siunčiama...
                  </>
                ) : (
                  'Siųsti atkūrimo nuorodą'
                )}
              </button>

              {/* Info text */}
              <div className="text-center">
                <p className="text-gray-400 text-xs">
                  Prisimenate slaptažodį?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      handleClose();
                      setTimeout(() => {
                        const event = new CustomEvent('openAuthModal', { detail: { mode: 'login' } });
                        window.dispatchEvent(event);
                      }, 100);
                    }}
                    disabled={isSubmitting}
                    className="text-primary-400 hover:text-primary-300 transition-colors disabled:opacity-50"
                  >
                    Grįžti prie prisijungimo
                  </button>
                </p>
              </div>
            </form>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordModal; 